// Order pricing engine (storefront preview).
//
// KEEP IN SYNC with functions/pricingEngine.js. That copy is authoritative —
// whatever is stored on an order comes from the server. This one exists so the
// cart, checkout and book page can show a total before anything is submitted.
//
// Rules are loaded from `settings/pricing` via fetchPricing() and are described
// by three independent choices:
//
//   scope  "item"  -> measured against one product line
//          "order" -> measured against the whole cart
//   basis  "quantity" | "amount"
//   zoneId -> the shipping zone it applies to (free delivery only)

export const DEFAULT_ZONE_ID = "rest_of_world";

export function round2(n) {
  return Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100;
}

function normaliseCountry(value) {
  return String(value || "").trim().toLowerCase();
}

function lineTotal(line) {
  return (Number(line.price) || 0) * (Number(line.qty) || 0);
}

export function resolveZone(zones, country) {
  const list = Array.isArray(zones) ? zones : [];
  const key = normaliseCountry(country);
  if (key) {
    const hit = list.find((z) =>
      (Array.isArray(z.countries) ? z.countries : []).some(
        (c) => normaliseCountry(c) === key,
      ),
    );
    if (hit) return hit;
  }
  return (
    list.find((z) => z.isDefault) ||
    list.find((z) => z.id === DEFAULT_ZONE_ID) ||
    null
  );
}

function measure(rule, ctx) {
  if (rule.scope === "order") {
    return rule.basis === "amount" ? ctx.subtotal : ctx.itemCount;
  }
  const line = ctx.items.find((i) => i.productId === rule.productId);
  if (!line) return 0;
  return rule.basis === "amount" ? lineTotal(line) : Number(line.qty) || 0;
}

function ruleApplies(rule, ctx, zoneId) {
  if (!rule || rule.enabled === false) return false;
  if (rule.zoneId && rule.zoneId !== zoneId) return false;
  return measure(rule, ctx) >= (Number(rule.threshold) || 0);
}

function discountAmount(rule, ctx) {
  const value = Number(rule.value) || 0;

  if (rule.scope === "order") {
    if (rule.type === "percent") return round2((ctx.subtotal * value) / 100);
    if (rule.type === "fixed_amount") return round2(Math.min(value, ctx.subtotal));
    return 0;
  }

  const line = ctx.items.find((i) => i.productId === rule.productId);
  if (!line) return 0;
  const total = lineTotal(line);

  if (rule.type === "fixed_unit_price") {
    const off = Math.max(0, (Number(line.price) || 0) - value);
    return round2(off * (Number(line.qty) || 0));
  }
  if (rule.type === "percent") return round2((total * value) / 100);
  if (rule.type === "fixed_amount") return round2(Math.min(value, total));
  return 0;
}

/** A stated delivery time, or null when the client has not given one. */
function leadDays(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** A rate the client has not filled in. */
function unpriced(zone) {
  return (
    !zone ||
    zone.rate === null ||
    zone.rate === undefined ||
    zone.rate === ""
  );
}

function baseShipping(zone, ctx) {
  // Only with no zones at all does the product's own cost apply. A zone that
  // exists but has a blank rate must NOT fall back to it — leaving a region
  // blank is how the client routes that region to an enquiry.
  if (!zone) {
    return round2(
      ctx.items.reduce(
        (sum, i) => sum + (Number(i.shippingCost) || 0) * (Number(i.qty) || 0),
        0,
      ),
    );
  }
  const rate = Number(zone.rate) || 0;
  if (zone.rateType === "per_order") return round2(rate);
  return round2(rate * ctx.itemCount);
}

function nextThreshold(rules, ctx, zoneId) {
  let best = null;
  for (const rule of rules) {
    if (!rule || rule.enabled === false) continue;
    if (rule.zoneId && rule.zoneId !== zoneId) continue;
    if (rule.basis !== "quantity") continue;
    const have = measure(rule, ctx);
    const need = Number(rule.threshold) || 0;
    if (have >= need) continue;
    const remaining = need - have;
    if (!best || remaining < best.remaining) {
      best = { remaining, threshold: need, label: rule.label || "" };
    }
  }
  return best;
}

/**
 * Price a cart for display.
 *
 * @param {Array}  items    [{ productId, title, price, qty, shippingCost }]
 * @param {Object} settings the `settings/pricing` document
 * @param {String} country  delivery country
 */
export function priceOrder(items, settings, country) {
  const list = Array.isArray(items) ? items : [];
  const cfg = settings || {};
  const zones = Array.isArray(cfg.zones) ? cfg.zones : [];

  const subtotal = round2(list.reduce((s, i) => s + lineTotal(i), 0));
  const itemCount = list.reduce((s, i) => s + (Number(i.qty) || 0), 0);
  const ctx = { items: list, subtotal, itemCount };

  const zone = resolveZone(zones, country);
  const zoneId = zone ? zone.id : null;

  // A zone switched off, or left without a rate, cannot be checked out online:
  // the storefront offers an enquiry instead.
  const requiresEnquiry = Boolean(
    zone && (zone.enabled === false || unpriced(zone)),
  );

  const discountRules = Array.isArray(cfg.discounts) ? cfg.discounts : [];
  const deliveryRules = Array.isArray(cfg.freeDelivery) ? cfg.freeDelivery : [];

  const discountLines = [];
  for (const rule of discountRules) {
    if (!ruleApplies(rule, ctx, zoneId)) continue;
    const amount = discountAmount(rule, ctx);
    if (amount > 0) {
      discountLines.push({
        ruleId: rule.id || "",
        label: rule.label || "Discount",
        amount,
      });
    }
  }
  const discount = round2(
    Math.min(subtotal, discountLines.reduce((s, d) => s + d.amount, 0)),
  );

  const freeRule = deliveryRules.find((r) => ruleApplies(r, ctx, zoneId)) || null;
  const shippingBeforeFree = baseShipping(zone, ctx);
  const shipping = freeRule ? 0 : shippingBeforeFree;

  return {
    currency: cfg.currency || "SGD",
    itemCount,
    subtotal,
    discount,
    discountLines,
    shipping,
    shippingBeforeFree,
    freeDelivery: Boolean(freeRule),
    freeDeliveryLabel: freeRule ? freeRule.label || "Free delivery" : "",
    zoneId,
    zoneLabel: zone ? zone.label || zone.id : "",
    // Minimum working days to fulfil, so the order can record what the buyer
    // was told at the time rather than whatever the rule says later. Null when
    // unset — Number(null) is 0, which would promise same-day delivery.
    leadTimeDays: zone ? leadDays(zone.leadTimeDays) : null,
    requiresEnquiry,
    total: round2(Math.max(0, subtotal - discount + shipping)),
    nextFreeDelivery: nextThreshold(deliveryRules, ctx, zoneId),
    nextDiscount: nextThreshold(discountRules, ctx, zoneId),
  };
}

/** Every country the rules name, for the checkout country selector. */
export function zoneCountries(settings) {
  const zones = (settings && settings.zones) || [];
  const all = [];
  for (const z of zones) {
    for (const c of z.countries || []) if (!all.includes(c)) all.push(c);
  }
  return all.sort((a, b) => a.localeCompare(b));
}
